import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import LoadingImage from "#/components/shared/LoadingImage";
import { ImageModal } from "#/components/chat/image-modal";
import { ThemedModal } from "#/components/shared/ThemedModal";
import Typography from "#/components/shared/typography";
import { MediaItem } from "#/lib/mediaApi";
import { buildMediaUrl } from "#/lib/mediaUrls";
import { useCreateMediaEmbedding } from "#/features/media/hooks/useCreateMediaEmbedding";
import { useMediaEmbeddingStatus } from "#/features/media/hooks/useMediaEmbeddingStatus";
import { MediaGenerateForm } from "#/features/media/components/MediaGenerateForm";

export type MediaDetailsPanelProps = {
  item: MediaItem | null;
  onClose: () => void;
  onCopyUrl: (item: MediaItem) => void;
  onCopyImg: (item: MediaItem) => void;
  onCopyMarkdown: (item: MediaItem) => void;
};

function formatDate(value?: string | null) {
  if (!value) {
    return "—";
  }

  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return value;
  }

  return new Date(parsed).toLocaleString();
}

export function MediaDetailsPanel({
  item,
  onClose,
  onCopyUrl,
  onCopyImg,
  onCopyMarkdown,
}: MediaDetailsPanelProps) {
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [isPollingEmbedding, setIsPollingEmbedding] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutate: createEmbedding, isPending: isEmbedding } =
    useCreateMediaEmbedding();
  const { data: embeddingStatus } = useMediaEmbeddingStatus(
    item?.id,
    isPollingEmbedding
  );

  useEffect(() => {
    if (!embeddingStatus?.has_embedding) return;
    setIsPollingEmbedding(false);
    queryClient.invalidateQueries({ queryKey: ["media", "search"] });
  }, [embeddingStatus?.has_embedding, queryClient]);

  if (!item) {
    return (
      <div className="rounded-2xl border border-cream-200 bg-white p-6 text-center text-brown-600">
        <Typography variant="subtitle1">Select a media row</Typography>
        <Typography variant="body2" className="mt-2 text-brown-500">
          Click a row to view metadata and copy actions.
        </Typography>
      </div>
    );
  }

  const altText = item.name ?? item.slug ?? "Media thumbnail";
  const imageUrl = buildMediaUrl(item.url ?? item.slug ?? "");
  const hasEmbedding =
    embeddingStatus?.has_embedding ?? item.has_embedding ?? null;
  const subjectContext =
    item.subject === "blog" ||
    item.subject === "site" ||
    item.subject === "marketing" ||
    item.subject === "content" ||
    item.subject === "general"
      ? item.subject
      : undefined;

  return (
    <aside className="rounded-2xl border border-cream-200 bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <Typography variant="subtitle1" className="text-brown-800">
            Media details
          </Typography>
          <Typography variant="caption" className="text-brown-500">
            {item.name ?? item.slug ?? "Media asset"}
          </Typography>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <ActionButton
              size="sm"
              tone="secondary"
              onClick={() => onCopyUrl(item)}
            >
              Copy URL
            </ActionButton>
            <ActionButton
              size="sm"
              tone="secondary"
              onClick={() => navigate(`/admin/content/media/${item.id}`)}
            >
              Fullscreen Details
            </ActionButton>
            <ActionButton
              size="sm"
              tone="secondary"
              onClick={() => setIsGenerateOpen(true)}
            >
              Generate From Image
            </ActionButton>
            <ActionButton
              size="sm"
              tone="secondary"
              onClick={() => setIsImageOpen(true)}
            >
              View Full Image
            </ActionButton>
            <ActionButton
              size="sm"
              tone="secondary"
              onClick={() => onCopyImg(item)}
            >
              Copy &lt;img&gt;
            </ActionButton>
            <ActionButton
              size="sm"
              tone="secondary"
              onClick={() => onCopyMarkdown(item)}
            >
              Copy Markdown
            </ActionButton>
            {!item.has_embedding && (
              <ActionButton
                size="sm"
                tone="secondary"
                onClick={() =>
                  createEmbedding(item.id, {
                    onSuccess: () => setIsPollingEmbedding(true),
                  })
                }
                disabled={isEmbedding}
              >
                {isEmbedding ? "Creating Embedding..." : "Create Embedding"}
              </ActionButton>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-cream-200 px-3 py-1 text-xs text-brown-600 hover:bg-cream-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 h-40 w-full">
        <LoadingImage src={imageUrl} alt={altText} className="rounded-xl" />
      </div>

      <div className="mt-5 space-y-3 text-sm text-brown-700">
        <div>
          <Typography variant="caption" className="text-brown-500">
            URL
          </Typography>
          {item.url ? (
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="break-all text-sm text-terracotta-600 underline underline-offset-2"
            >
              {item.url}
            </a>
          ) : (
            <Typography variant="body2">—</Typography>
          )}
        </div>
        <div>
          <Typography variant="caption" className="text-brown-500">
            Slug
          </Typography>
          <Typography variant="body2">{item.slug ?? "—"}</Typography>
        </div>
        <div>
          <Typography variant="caption" className="text-brown-500">
            Type
          </Typography>
          <Typography variant="body2">{item.image_type ?? "—"}</Typography>
        </div>
        <div>
          <Typography variant="caption" className="text-brown-500">
            Subject
          </Typography>
          <Typography variant="body2">{item.subject ?? "—"}</Typography>
        </div>
        <div>
          <Typography variant="caption" className="text-brown-500">
            Active
          </Typography>
          <Typography variant="body2">
            {item.is_active === null
              ? "—"
              : item.is_active
              ? "Yes"
              : "No"}
          </Typography>
        </div>
        <div>
          <Typography variant="caption" className="text-brown-500">
            Embedding
          </Typography>
          <Typography variant="body2">
            {isPollingEmbedding
              ? "Creating..."
              : hasEmbedding === null || hasEmbedding === undefined
              ? "—"
              : hasEmbedding
              ? "Yes"
              : "No"}
          </Typography>
        </div>
        <div>
          <Typography variant="caption" className="text-brown-500">
            Created
          </Typography>
          <Typography variant="body2">{formatDate(item.created_at)}</Typography>
        </div>
        <div>
          <Typography variant="caption" className="text-brown-500">
            Updated
          </Typography>
          <Typography variant="body2">{formatDate(item.updated_at)}</Typography>
        </div>
      </div>

      <div className="mt-5 space-y-3 text-sm text-brown-700">
        <div>
          <Typography variant="caption" className="text-brown-500">
            Prompt
          </Typography>
          <Typography variant="body2" className="break-words">
            {item.prompt ?? "—"}
          </Typography>
        </div>
        <div>
          <Typography variant="caption" className="text-brown-500">
            Relevant text
          </Typography>
          <div className="max-h-40 overflow-auto rounded-md border border-cream-200 bg-cream-50 p-2">
            <Typography
              variant="body2"
              className="whitespace-pre-wrap break-words"
            >
              {item.relevant_text ?? "—"}
            </Typography>
          </div>
        </div>
        <div>
          <Typography variant="caption" className="text-brown-500">
            URL match
          </Typography>
          <Typography variant="body2" className="break-words">
            {item.url_match ?? "—"}
          </Typography>
        </div>
        <div>
          <Typography variant="caption" className="text-brown-500">
            Insert regex
          </Typography>
          <Typography variant="body2" className="break-words">
            {item.insert_regex ?? "—"}
          </Typography>
        </div>
      </div>

      <ImageModal
        isOpen={isImageOpen}
        onClose={() => setIsImageOpen(false)}
        imageSrc={imageUrl}
        imageAlt={altText}
      />

      <ThemedModal
        isOpen={isGenerateOpen}
        onClose={() => setIsGenerateOpen(false)}
        title="Generate from image"
        description="Create a new variant using this image as the base."
        tone="terracotta"
      >
        <MediaGenerateForm
          initialItem={item}
          initialContext={subjectContext}
          initialSourceMode="existing"
          onComplete={() => setIsGenerateOpen(false)}
        />
      </ThemedModal>
    </aside>
  );
}

export default MediaDetailsPanel;
